import { Check, Link as LinkIcon, MoreVert } from "@mui/icons-material";
import {
  Box,
  ClickAwayListener,
  IconButton,
  ListItemText,
  MenuItem,
  Paper,
  Popper,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  useEffect,
  useRef,
  useState,
  type ElementType,
  type MouseEvent,
  type ReactNode,
} from "react";
import { ConfirmationModal } from "src/components/ConfirmationModal";
import { UserAvatar } from "src/components/UserAvatar";
import { CommentInput } from "./CommentInput";
import {
  replaceEmailMentionsForDisplay,
  stripMarkdownForDisplay,
} from "./commentMarkdownUtils";
import {
  formatTiptapCollaborationTime,
  getTiptapCommentAuthorDisplay,
  getTiptapCommentAuthorId,
  getTiptapCommentContentText,
  isDovetailAgentTiptapComment,
  toTiptapCommentDate,
} from "./tiptapCommentUtils";
import type {
  TiptapComment,
  TiptapCommentsController,
  TiptapCommentThread,
} from "./types";

type TiptapCollaborationCardFrameProps = {
  borderColor?: string;
  children: ReactNode;
  component?: ElementType;
  dataAttributes?: Record<`data-${string}`, string | undefined>;
  highlighted: number;
  highlightAnimationPrefix: string;
};

type TiptapThreadDiscussionVariant = "document" | "suggestion";

type TiptapThreadDiscussionProps = {
  controller: TiptapCommentsController;
  getThreadLinkUrl?: (threadId: string) => string;
  thread: TiptapCommentThread;
  variant: TiptapThreadDiscussionVariant;
  visibleComments: TiptapComment[];
};

const getCommentQuotePreview = (quote: string) => {
  const displayQuote = stripMarkdownForDisplay(quote);
  return displayQuote.length <= 100
    ? displayQuote
    : `${displayQuote.slice(0, 100)}...`;
};

export const TiptapCollaborationCardFrame = ({
  borderColor = "rgb(229, 231, 235)",
  children,
  component,
  dataAttributes,
  highlighted,
  highlightAnimationPrefix,
}: TiptapCollaborationCardFrameProps) => {
  const highlightAnimationName = `${highlightAnimationPrefix}-${highlighted}`;
  const componentProps = component ? { component } : {};

  return (
    <Box
      {...componentProps}
      {...dataAttributes}
      style={{
        ...(highlighted
          ? { animation: `${highlightAnimationName} 2s ease-out` }
          : {}),
        backgroundColor: "#ffffff",
        border: `1px solid ${borderColor}`,
        borderRadius: 8,
        boxSizing: "border-box",
        padding: 12,
        width: "100%",
      }}
      sx={
        highlighted
          ? {
              [`@keyframes ${highlightAnimationName}`]: {
                "0%": {
                  backgroundColor: "#fef08a",
                  boxShadow: "0 0 0 2px #facc15",
                },
                "100%": {
                  backgroundColor: "transparent",
                  boxShadow: "none",
                },
              },
            }
          : undefined
      }
    >
      {children}
    </Box>
  );
};

export const TiptapThreadDiscussion = ({
  controller,
  getThreadLinkUrl,
  thread,
  variant,
  visibleComments,
}: TiptapThreadDiscussionProps) => {
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(
    null
  );
  const [linkCopied, setLinkCopied] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [menuCommentId, setMenuCommentId] = useState<string | null>(null);
  const resetLinkCopiedTimeoutRef = useRef<number | null>(null);
  const firstVisibleCommentId = visibleComments[0]?.id ?? null;
  const unresolved = !thread.resolvedAt;
  const canReply = !controller.disabled && unresolved;

  useEffect(
    () => () => {
      if (resetLinkCopiedTimeoutRef.current !== null) {
        window.clearTimeout(resetLinkCopiedTimeoutRef.current);
      }
    },
    []
  );

  if (!visibleComments.length && !canReply) {
    return null;
  }

  const getAuthorId = (comment: TiptapComment) =>
    getTiptapCommentAuthorId(comment, thread);

  const canMutateComment = (comment: TiptapComment) =>
    Boolean(
      !controller.disabled &&
        (variant !== "document" ||
          !isDovetailAgentTiptapComment(comment, thread)) &&
        controller.currentUser.id &&
        getAuthorId(comment) === controller.currentUser.id
    );

  const isFirstVisibleComment = (commentId: string) =>
    firstVisibleCommentId === commentId;

  const isThreadDelete = (commentId: string | null) =>
    variant === "document" &&
    Boolean(commentId && isFirstVisibleComment(commentId));

  const handleResolve = () => {
    if (controller.disabled) {
      return;
    }

    if (unresolved) {
      controller.resolveThread(thread.id);
    } else {
      controller.reopenThread(thread.id);
    }
  };

  const handleCopyLink = async () => {
    if (!getThreadLinkUrl) {
      return;
    }

    await navigator.clipboard.writeText(getThreadLinkUrl(thread.id));
    setLinkCopied(true);
    if (resetLinkCopiedTimeoutRef.current !== null) {
      window.clearTimeout(resetLinkCopiedTimeoutRef.current);
    }
    resetLinkCopiedTimeoutRef.current = window.setTimeout(() => {
      setLinkCopied(false);
      resetLinkCopiedTimeoutRef.current = null;
    }, 2000);
  };

  const handleOpenCommentMenu = (
    event: MouseEvent<HTMLElement>,
    commentId: string
  ) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuCommentId(commentId);
  };

  const handleCloseCommentMenu = () => {
    setMenuAnchorEl(null);
    setMenuCommentId(null);
  };

  const handleMenuEdit = () => {
    if (!controller.disabled && menuCommentId) {
      setEditingCommentId(menuCommentId);
    }
    handleCloseCommentMenu();
  };

  const handleDeleteClick = (commentId: string) => {
    if (controller.disabled) {
      return;
    }

    setDeletingCommentId(commentId);
    setDeleteDialogOpen(true);
  };

  const handleMenuDelete = () => {
    if (menuCommentId) {
      handleDeleteClick(menuCommentId);
    }
    handleCloseCommentMenu();
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDeletingCommentId(null);
  };

  const handleDeleteConfirm = () => {
    if (controller.disabled || !deletingCommentId) {
      return;
    }

    if (isThreadDelete(deletingCommentId)) {
      controller.deleteThread(thread.id);
    } else {
      controller.deleteComment(thread.id, deletingCommentId);
    }

    setDeleteDialogOpen(false);
    setDeletingCommentId(null);
  };

  const selectedMenuComment = menuCommentId
    ? visibleComments.find((candidate) => candidate.id === menuCommentId)
    : null;

  return (
    <Box
      data-testid={
        variant === "document"
          ? undefined
          : `tiptap-suggestion-thread-${thread.id}`
      }
      style={
        variant === "document"
          ? undefined
          : {
              borderTop: "1px solid rgb(243, 244, 246)",
              marginTop: 12,
              paddingTop: 12,
            }
      }
    >
      {visibleComments.length ? (
        <Box style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {visibleComments.map((comment, index) => {
            const authorDisplay = getTiptapCommentAuthorDisplay({
              comment,
              orgUsers: controller.orgUsers,
              thread,
            });
            const commentContent = getTiptapCommentContentText(comment.content);
            const canMutate = canMutateComment(comment);
            const isFirstComment = isFirstVisibleComment(comment.id);
            // Natively-created threads carry the highlighted text on
            // `thread.data.quote`, but threads created via Tiptap's
            // create-thread REST API (e.g. the legacy comment migration) put
            // the thread `data` on the first comment instead. Prefer the
            // thread, fall back to the first comment.
            const commentQuote =
              (thread.data?.quote ?? comment.data?.quote)?.trim() ?? "";
            const commentMetadata = [
              authorDisplay.coAuthorName
                ? `co-authored by ${authorDisplay.coAuthorName}`
                : authorDisplay.onBehalfOfName
                  ? `on behalf of ${authorDisplay.onBehalfOfName}`
                  : null,
              formatTiptapCollaborationTime(
                toTiptapCommentDate(comment.createdAt)
              ),
            ]
              .filter(Boolean)
              .join(" - ");

            return (
              <Box
                key={comment.id}
                data-testid={`${
                  variant === "document"
                    ? "tiptap-comment"
                    : "tiptap-suggestion-comment"
                }-${comment.id}`}
                style={
                  index > 0
                    ? {
                        borderTop: "1px solid rgb(243, 244, 246)",
                        paddingTop: 12,
                      }
                    : undefined
                }
              >
                <Box
                  data-testid={`${
                    variant === "document"
                      ? "tiptap-comment-header"
                      : "tiptap-suggestion-comment-header"
                  }-${comment.id}`}
                  style={{
                    alignItems: "flex-start",
                    display: "flex",
                    gap: 8,
                  }}
                >
                  <UserAvatar
                    userId={authorDisplay.avatarUserId}
                    name={authorDisplay.avatarName}
                    imageSrc={authorDisplay.avatarSrc}
                    imageAlt={authorDisplay.avatarName}
                    size={21}
                    fontSize={9}
                  />
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Box
                      style={{
                        alignItems: "flex-start",
                        display: "flex",
                        gap: 4,
                        justifyContent: "space-between",
                      }}
                    >
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body1"
                          style={{ color: "rgb(17, 24, 39)" }}
                        >
                          {authorDisplay.authorName}
                        </Typography>
                      </Box>
                      {editingCommentId !== comment.id && (
                        <Box
                          style={{
                            alignItems: "center",
                            display: "flex",
                            flexShrink: 0,
                            gap: 4,
                          }}
                        >
                          {variant === "document" && isFirstComment ? (
                            <>
                              {getThreadLinkUrl ? (
                                <Tooltip
                                  title={linkCopied ? "Copied!" : "Copy link"}
                                >
                                  <IconButton
                                    aria-label="Copy link"
                                    size="small"
                                    onClick={handleCopyLink}
                                    style={{ padding: 0 }}
                                  >
                                    <LinkIcon
                                      fontSize="small"
                                      color={linkCopied ? "success" : "action"}
                                    />
                                  </IconButton>
                                </Tooltip>
                              ) : null}
                              <Tooltip
                                title={
                                  unresolved ? "Mark as resolved" : "Reopen"
                                }
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    aria-label={
                                      unresolved ? "Mark as resolved" : "Reopen"
                                    }
                                    onClick={handleResolve}
                                    disabled={controller.disabled}
                                    style={{ padding: 0 }}
                                  >
                                    <Check
                                      fontSize="small"
                                      color={unresolved ? "action" : "success"}
                                    />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </>
                          ) : null}
                          {canMutate ? (
                            <IconButton
                              size="small"
                              onClick={(event) =>
                                handleOpenCommentMenu(event, comment.id)
                              }
                              aria-label={
                                variant === "document"
                                  ? "More options"
                                  : "More comment options"
                              }
                              sx={{
                                "&:hover": {
                                  bgcolor: "action.hover",
                                },
                                padding: 0,
                              }}
                            >
                              <MoreVert fontSize="small" />
                            </IconButton>
                          ) : null}
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>

                <Typography
                  variant="caption"
                  style={{
                    color: "rgb(107, 114, 128)",
                    display: "block",
                    marginTop: 4,
                  }}
                >
                  {commentMetadata}
                </Typography>

                {editingCommentId === comment.id && !controller.disabled ? (
                  <Box style={{ marginTop: 4 }}>
                    <CommentInput
                      onSubmit={(content) => {
                        controller.editComment(thread.id, comment.id, content);
                        setEditingCommentId(null);
                      }}
                      onCancel={() => setEditingCommentId(null)}
                      placeholder="Edit your comment... Use @ to mention"
                      submitLabel="Save"
                      autoFocus
                      orgUsers={controller.orgUsers}
                      initialValue={commentContent}
                    />
                  </Box>
                ) : (
                  <>
                    {variant === "document" &&
                    isFirstComment &&
                    commentQuote ? (
                      <Box
                        data-testid="tiptap-comment-quote"
                        style={{
                          borderLeft: "2px solid #e5e7eb",
                          marginBottom: 8,
                          marginTop: 8,
                          paddingLeft: 10,
                        }}
                      >
                        <Typography
                          variant="body2"
                          style={{
                            color: "rgb(107, 114, 128)",
                            fontStyle: "italic",
                          }}
                        >
                          {getCommentQuotePreview(commentQuote)}
                        </Typography>
                      </Box>
                    ) : null}
                    <Typography
                      variant="body1"
                      style={{
                        color: "rgb(55, 65, 81)",
                        fontSize: "1rem",
                        marginTop: 4,
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {replaceEmailMentionsForDisplay(
                        commentContent,
                        controller.orgUsers
                      )}
                    </Typography>
                  </>
                )}
              </Box>
            );
          })}
        </Box>
      ) : null}

      {canReply ? (
        <Box
          style={
            variant === "document"
              ? {
                  borderTop: "1px solid rgb(243, 244, 246)",
                  marginTop: 12,
                  paddingTop: 12,
                }
              : {
                  marginTop: visibleComments.length ? 12 : 0,
                }
          }
        >
          <CommentInput
            onSubmit={(content) => {
              controller.createReply(thread.id, content);
            }}
            placeholder={
              variant === "document"
                ? "Reply... Use @ to mention"
                : "Comment on suggestion... Use @ to mention"
            }
            submitLabel={variant === "document" ? "Reply" : "Comment"}
            orgUsers={controller.orgUsers}
            showActionsOnFocusOnly
          />
        </Box>
      ) : null}

      <Popper
        open={Boolean(menuAnchorEl) && !controller.disabled}
        anchorEl={menuAnchorEl}
        placement="bottom-end"
        modifiers={[
          { name: "offset", options: { offset: [0, 4] } },
          {
            name: "preventOverflow",
            options: { boundary: "clippingParents", padding: 8 },
          },
        ]}
        sx={{ zIndex: 1400 }}
      >
        <ClickAwayListener onClickAway={handleCloseCommentMenu}>
          <Paper elevation={8} sx={{ minWidth: 140, py: 0.5 }}>
            {selectedMenuComment && canMutateComment(selectedMenuComment) ? (
              <>
                <MenuItem onClick={handleMenuEdit}>
                  <ListItemText>
                    {variant === "document" ? "Edit Comment" : "Edit comment"}
                  </ListItemText>
                </MenuItem>
                <MenuItem onClick={handleMenuDelete}>
                  <ListItemText>
                    {isThreadDelete(selectedMenuComment.id)
                      ? "Delete thread"
                      : "Delete comment"}
                  </ListItemText>
                </MenuItem>
              </>
            ) : null}
          </Paper>
        </ClickAwayListener>
      </Popper>

      <ConfirmationModal
        open={deleteDialogOpen}
        title={
          isThreadDelete(deletingCommentId)
            ? "Delete thread?"
            : "Delete comment?"
        }
        content={
          isThreadDelete(deletingCommentId)
            ? "This will delete the entire thread and all comments. This action cannot be undone."
            : "This action cannot be undone."
        }
        confirmText="Delete"
        onAbort={handleDeleteCancel}
        onConfirm={controller.disabled ? undefined : handleDeleteConfirm}
      />
    </Box>
  );
};
